import React, { useState } from 'react';
import { Play, Download, Target } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { exportToCSV } from '../../utils/export';

const s = {
  page: { padding: '2rem', maxWidth: 1200, margin: '0 auto' },
  h1: { fontFamily: 'Playfair Display, serif', color: 'var(--text)', fontSize: '1.75rem', marginBottom: 4 },
  desc: { fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.9rem', marginBottom: '1.5rem', maxWidth: 700, lineHeight: 1.6 },
  btn: { fontFamily: 'DM Sans, sans-serif', background: 'var(--primary)', color: 'var(--bg)', border: 'none', padding: '10px 24px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnOutline: { fontFamily: 'DM Sans, sans-serif', background: 'transparent', color: 'var(--secondary)', border: '1px solid var(--secondary)', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 6 },
  actions: { display: 'flex', gap: 12, marginBottom: '1.5rem', alignItems: 'center' },
  card: { background: 'var(--card-bg)', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontFamily: 'DM Sans, sans-serif' },
  th: { textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.1)', color: 'var(--secondary)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
  td: { padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.05)', color: 'var(--text)', fontSize: '0.88rem' },
  spinner: { display: 'inline-block', width: 18, height: 18, border: '2px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  sortBtn: { background: 'none', border: 'none', color: 'var(--secondary)', cursor: 'pointer', fontSize: '0.7rem', marginLeft: 4 },
};

const fallbackData = [
  { name: 'Jake T.', currentSpend: '$1,680', harvestThreshold: '$1,800', gap: '$120', gapVisits: 8, recommendedOffer: '2x points on next 5 visits', likelihood: 0.91 },
  { name: 'Emma W.', currentSpend: '$1,720', harvestThreshold: '$1,800', gap: '$80', gapVisits: 5, recommendedOffer: 'Free upgrade on specialty drinks', likelihood: 0.88 },
  { name: 'Aisha B.', currentSpend: '$1,695', harvestThreshold: '$1,800', gap: '$105', gapVisits: 7, recommendedOffer: 'Bonus $15 when you spend $100', likelihood: 0.85 },
  { name: 'Carlos R.', currentSpend: '$1,710', harvestThreshold: '$1,800', gap: '$90', gapVisits: 6, recommendedOffer: 'Exclusive seasonal tasting event', likelihood: 0.82 },
  { name: 'Priya N.', currentSpend: '$1,740', harvestThreshold: '$1,800', gap: '$60', gapVisits: 4, recommendedOffer: 'Add a pastry, earn 3x points', likelihood: 0.78 },
  { name: 'Noah D.', currentSpend: '$1,685', harvestThreshold: '$1,800', gap: '$115', gapVisits: 8, recommendedOffer: 'Bring a friend bonus', likelihood: 0.74 },
  { name: 'Olivia M.', currentSpend: '$1,750', harvestThreshold: '$1,800', gap: '$50', gapVisits: 3, recommendedOffer: 'Weekend brunch bundle', likelihood: 0.72 },
  { name: 'Wei Z.', currentSpend: '$1,700', harvestThreshold: '$1,800', gap: '$100', gapVisits: 7, recommendedOffer: 'Try our new wellness line', likelihood: 0.68 },
];

export default function NearHarvest() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('likelihood');
  const [sortDir, setSortDir] = useState('desc');

  const runModel = async () => {
    setLoading(true);
    try {
      const res = await api.post('/models/near-harvest');
      setResults(res.data?.results || fallbackData);
      toast.success('Near Harvest model complete');
    } catch {
      setResults(fallbackData);
      toast.success('Near Harvest model complete (demo data)');
    } finally {
      setLoading(false);
    }
  };

  const sorted = [...(results || [])].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
    const an = typeof av === 'string' ? parseFloat(av.replace(/[^-\d.]/g, '')) : NaN;
    const bn = typeof bv === 'string' ? parseFloat(bv.replace(/[^-\d.]/g, '')) : NaN;
    if (!isNaN(an) && !isNaN(bn)) return sortDir === 'asc' ? an - bn : bn - an;
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });
  const toggleSort = (key) => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('desc'); } };

  return (
    <div style={s.page}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      <h1 style={s.h1}>Near Harvest</h1>
      <p style={s.desc}>
        Identifies Bloom customers within $120 of the Harvest tier threshold. Shows the gap amount,
        estimated visits to close, and personalized offers to accelerate progression.
      </p>

      <div style={s.actions}>
        <button style={s.btn} onClick={runModel} disabled={loading}>
          {loading ? <><span style={s.spinner} /> Running...</> : <><Play size={16} /> Run Model</>}
        </button>
        {results && (
          <button style={s.btnOutline} onClick={() => exportToCSV(results, 'near_harvest')}>
            <Download size={14} /> Export CSV
          </button>
        )}
      </div>

      {results && (
        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>
                {[['name','Customer'],['currentSpend','Current Spend'],['gap','Gap to Harvest'],['gapVisits','Est. Visits'],['recommendedOffer','Recommended Offer'],['likelihood','Likelihood']].map(([k,l]) => (
                  <th key={k} style={s.th}>{l}<button style={s.sortBtn} onClick={() => toggleSort(k)}>{sortKey === k ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <tr key={i}>
                  <td style={s.td}>{row.name}</td>
                  <td style={s.td}>{row.currentSpend}</td>
                  <td style={{ ...s.td, color: 'var(--secondary)', fontWeight: 600 }}>{row.gap}</td>
                  <td style={s.td}>{row.gapVisits}</td>
                  <td style={s.td}>{row.recommendedOffer}</td>
                  <td style={s.td}>{(row.likelihood * 100).toFixed(0)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
